import { useEffect, useState } from "react"
import { useService } from "../context/useService"
import { useSession, useSupabase } from "../context/useSupabase"
import { useToast } from "../context/useToast"
import { useLoading } from "../utils/useLoading"

export const useETLs = () => {
  const [dataset, setDataset] = useState(null)
  const [date, setDate] = useState(null)
  const [uploadHistory, setUploadHistory] = useState([])
  const { isLoading, setIsLoading } = useLoading()
  const toast = useToast()
  const session = useSession()
  const supabase = useSupabase()
  const { etlService } = useService()

  const datasets = [
    {
      label: "Lote 45 - All Trading Desks Overview",
      value: "lote45-all-trading-desks-overview",
      howToGet: "Export the All Trading Desks Overview from Lote 45 (Tools > Files Export > All Trading Desks Overview).",
      accept: ".txt",
    },
    {
      label: "Bloomberg - Benchmarks (Offshore)",
      value: "bloomberg-manual-exporting-benchmarks-offshore",
      howToGet: "Export the benchmarks from Bloomberg, saved on our SharePoint (Gestão > DL > Bloomberg > ETL TM > ETL_Benchmark_Offshore.xlsx)",
      accept: ".xlsx",
    },
    {
      label: "Bloomberg - Benchmarks (Onshore)",
      value: "bloomberg-manual-exporting-benchmarks-onshore",
      howToGet: "Export the benchmarks from Bloomberg, saved on our SharePoint (Gestão > DL > Bloomberg > ETL TM > ETL_Benchmark_Onshore.xlsx)",
      accept: ".xlsx",
    },
    {
      label: "Prequin - Benchmarks - All Databases",
      value: "prequin-benchmarks",
      howToGet:
        'Go to <a href="https://pro.preqin.com/analysis/benchmarks/privateCapital/market">Prequin Benchmark Dashboard</a> and click on <b>Export</b>. You will receive the file by email.',
      accept: ".xlsx",
    },
    {
      label: "Cambridge Associates - Benchmarks - Venture Capital, Growth & Buyout (US)",
      value: "cambridge-associates-benchmarks-us-venture-capital-growth-buyout",
      howToGet:
        "Access the Cambridge Associates portal and filter the benchmarks by Venture Capital, Growth & Buyout, groping by vintage year. Then, export the data to Excel.",
      accept: ".xlsx",
    },
    {
      label: "Cambridge Associates - Benchmarks - Credit Opportunities, Infra e Real Estate (US)",
      value: "cambridge-associates-benchmarks-us-credit-opportunities-infrastructure-real-estate",
      howToGet:
        "Access the Cambridge Associates portal and filter the benchmarks by Credit Opportunities, Infrastructure & Real Estate, groping by vintage year. Then, export the data to Excel.",
      accept: ".xlsx",
    },
    {
      label: "Cambridge Associates - Benchmarks - Global Private Equity",
      value: "cambridge-associates-benchmarks-global-private-equity",
      howToGet:
        "Access the Cambridge Associates portal and filter the benchmarks by Global Private Equity, groping by vintage year. Then, export the data to Excel.",
      accept: ".xlsx",
    },
    {
      label: "Cambridge Associates - Benchmarks - Global Venture Capital",
      value: "cambridge-associates-benchmarks-global-venture-capital",
      howToGet:
        "Access the Cambridge Associates portal and filter the benchmarks by Global Venture Capital, groping by vintage year. Then, export the data to Excel.",
      accept: ".xlsx",
    },
    {
      label: "Cambridge Associates - Benchmarks - US Private Credit",
      value: "cambridge-associates-benchmarks-us-private-credit",
      howToGet:
        "Access the Cambridge Associates portal and filter the benchmarks by Private Credit, groping by vintage year. Then, export the data to Excel.",
      accept: ".xlsx",
    },
    {
      label: "Cambridge Associates - Benchmarks - US Secondaries",
      value: "cambridge-associates-benchmarks-us-secondaries",
      howToGet:
        "Access the Cambridge Associates portal and filter the benchmarks by Secondaries, groping by vintage year. Then, export the data to Excel.",
      accept: ".xlsx",
    },
    {
      label: "Cambridge Associates - Benchmarks - US Private Equity",
      value: "cambridge-associates-benchmarks-us-private-equity",
      howToGet:
        "Access the Cambridge Associates portal and filter the benchmarks by Private Equity to US industry, groping by vintage year. Then, export the data to Excel.",
      accept: ".xlsx",
    },
  ]

  const formatAccept = datasets.find((d) => d.value === dataset)?.accept

  const upload = async (file) => {
    if (formatAccept && !file.name.endsWith(formatAccept)) {
      return toast.error({ message: "Invalid file type", description: `Expected ${formatAccept}, but got ${file.name}` })
    }

    setIsLoading(true)

    try {
      await supabase.auth.refreshSession()
      await etlService.upload(session.access_token, file, dataset, date)
      toast.success({ message: "File successfully uploaded" })
      await loadHistory()
      setDataset(null)
    } catch (error) {
      console.error(error)
      toast.error({ message: "Error uploading file", description: "Try again in few moments." })
    } finally {
      setIsLoading(false)
    }

    return false
  }

  const loadHistory = async () => {
    try {
      await supabase.auth.refreshSession()
      const uploads = await etlService.getUploadedFiles(session.access_token)
      const formatted = uploads.map(({ date, name }) => ({
        date,
        name: datasets.find((d) => d.value === name)?.label || name,
      }))
      setUploadHistory(formatted)
    } catch (error) {
      console.error(error)
      toast.error({ message: "Error fetching upload history", description: "Please try again later." })
    }
  }

  const forceAddeparReprocessing = async () => {
    if (!session || !session.access_token) return
    await supabase.auth.refreshSession()
    await etlService.forceAddeparReprocessing(session.access_token)
    toast.success({ message: "Addepar reprocessing triggered successfully" })
  }

  useEffect(() => {
    if (!session || !session.access_token) return
    etlService.triggerColdStart(session.access_token)
    loadHistory()
  }, [session])

  return {
    uploadHistory,
    isLoading,
    date,
    setDate,
    dataset,
    upload,
    setDataset,
    datasets,
    formatAccept,
    forceAddeparReprocessing,
  }
}
